import React, { useState, useEffect, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import type { UserCredential } from 'firebase/auth';
import { auth } from '../firebase/config';
import { synchronizeDatabasePrincipal } from '../lib/supabase';
import { AuthContext, AuthContextType } from './AuthContextValue';
import { useTranslation } from 'react-i18next';
import { getDatabaseErrorTranslationKey } from '../lib/databaseErrors';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState<AuthContextType['currentUser']>(null);
  const [loading, setLoading] = useState(true);
  const [identityError, setIdentityError] = useState<Error | null>(null);
  const identitySequence = useRef(0);

  function signup(email: string, password: string): Promise<UserCredential> {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout(): Promise<void> {
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const sequence = ++identitySequence.current;
      setLoading(true);
      setCurrentUser(null);
      setIdentityError(null);
      synchronizeDatabasePrincipal(user).then(() => {
        if (sequence !== identitySequence.current) {
          return;
        }
        setIdentityError(null);
        setCurrentUser(user);
        setLoading(false);
      }).catch((error: unknown) => {
        if (sequence !== identitySequence.current) {
          return;
        }
        setIdentityError(error instanceof Error
          ? error
          : new Error('Unexpected database identity synchronization failure'));
        setLoading(false);
      });
    });

    return unsubscribe;
  }, []);

  if (identityError) {
    return (
      <div role="alert" className="m-6 rounded bg-red-100 p-4 text-red-800">
        {t(getDatabaseErrorTranslationKey(identityError, 'databaseIdentityError'))}
      </div>
    );
  }

  const value = {
    currentUser,
    user: currentUser,
    signup,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
